import { useDispatch, useSelector } from 'react-redux';
import { Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { HiBars3 } from 'react-icons/hi2';
import type { AppDispatch } from '@/store/store';
import { logoutUser, selectAuthUser, selectIsAuthenticated } from '@/store/authSlice';
import { useGetCurrentTenantQuery } from '@/store/tenantApi';
import { useGetMeQuery } from '@/store/meApi';
import { HeaderBar, HeaderLeft, HeaderRight, HeaderTitle, LanguageToggle, Avatar, UserDisplayName } from './Header.styled';

interface HeaderProps {
  onToggleSidebar: () => void;
}

export default function Header({ onToggleSidebar }: HeaderProps) {
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector(selectAuthUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { data: tenant } = useGetCurrentTenantQuery();
  // Display name is no longer a JWT claim — it comes from /auth/me (decrypted server-side).
  const { data: me } = useGetMeQuery(undefined, { skip: !isAuthenticated });
  const displayName = me?.displayName ?? user?.email ?? '';
  const { t, i18n } = useTranslation();

  const handleLogout = () => {
    dispatch(logoutUser());
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'pl' ? 'en' : 'pl';
    i18n.changeLanguage(newLang);
  };

  return (
    <HeaderBar>
      <HeaderLeft>
        <button
          className="btn btn-link d-lg-none p-0"
          onClick={onToggleSidebar}
          aria-label={t('header.toggleSidebar')}
        >
          <HiBars3 size="1.5rem" />
        </button>
        {tenant && <HeaderTitle>{tenant.name}</HeaderTitle>}
      </HeaderLeft>
      <HeaderRight>
        <LanguageToggle onClick={toggleLanguage} title="Switch language">
          {i18n.language === 'pl' ? 'EN' : 'PL'}
        </LanguageToggle>
        <Dropdown align="end">
          <Dropdown.Toggle variant="link" className="p-0 text-decoration-none d-flex align-items-center" id="user-menu">
            <div className="d-flex align-items-center gap-2">
              <Avatar>
                {displayName.charAt(0).toUpperCase() || 'U'}
              </Avatar>
              <div className="d-none d-md-inline text-start">
                <UserDisplayName>{displayName}</UserDisplayName>
              </div>
            </div>
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item onClick={handleLogout}>{t('header.signOut')}</Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </HeaderRight>
    </HeaderBar>
  );
}
